import { forwardRef, useImperativeHandle, useRef } from "react"

import YouTube from "react-youtube"

import type { YouTubeWebEmbedProps, YouTubeWebEmbedRef } from "./types"

export type { YouTubeWebEmbedRef, YouTubeWebEmbedProps }

/**
 * Web-only YouTube embed using the YouTube IFrame API via react-youtube.
 * Unlike react-native-youtube-iframe (which relies on window.ReactNativeWebView that
 * doesn't exist in browsers), this uses the IFrame API directly — real play/pause events
 * and synchronous getCurrentTime() that reflects actual video position, unaffected by ads.
 */
export const YouTubeWebEmbed = forwardRef<YouTubeWebEmbedRef, YouTubeWebEmbedProps>(
  ({ videoId, onReady, onPlay, onPause, onEnd, onError, onStateChange }, ref) => {
    const ytPlayerRef = useRef<any>(null)

    useImperativeHandle(ref, () => ({
      getCurrentTime: () => ytPlayerRef.current?.getCurrentTime() ?? 0,
      getDuration: () => ytPlayerRef.current?.getDuration() ?? 0,
      pauseVideo: () => ytPlayerRef.current?.pauseVideo(),
      playVideo: () => ytPlayerRef.current?.playVideo(),
      seekTo: (seconds: number) => ytPlayerRef.current?.seekTo(seconds, true),
    }))

    return (
      <YouTube
        videoId={videoId}
        style={{ width: "100%", height: "100%" }}
        opts={{
          width: "100%",
          height: "100%",
          playerVars: {
            autoplay: 0,
            controls: 1,
            iv_load_policy: 3,
            cc_lang_pref: "us",
            rel: 0,
            modestbranding: 1,
            // `start` and seekTo() in onReady are both intentionally omitted.
            // Either can trigger an onPlay event via the YouTube IFrame API on an
            // unstarted player, cutting off sidekick speech. The seek is deferred
            // to the parent's autoplay effect, where it runs after isSpeaking=false.
          },
        }}
        onReady={(event: any) => {
          ytPlayerRef.current = event.target
          onReady?.()
        }}
        onPlay={onPlay}
        onPause={onPause}
        onEnd={onEnd}
        onError={onError}
        onStateChange={(event: any) => onStateChange?.(event.data)}
      />
    )
  },
)

YouTubeWebEmbed.displayName = "YouTubeWebEmbed"
